import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle2, FileSignature, Loader2, ShieldCheck, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sign/${token}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.detail || "Unknown link"));
  }, [token]);

  const sign = async (e) => {
    e.preventDefault();
    if (!agreed) { toast.error("Please check the agreement box."); return; }
    if (name.trim().length < 2) { toast.error("Enter your full legal name."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/sign/${token}`, { typed_name: name.trim() });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signing failed");
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0E1A] text-white flex items-center justify-center p-8">
        <div className="max-w-md text-center border border-neutral-800 p-10 bg-[#141B2D]">
          <FileSignature className="w-12 h-12 mx-auto text-rose-500 mb-4" />
          <h1 className="font-display text-2xl font-bold mb-2">Link not found</h1>
          <p className="text-neutral-400 text-sm">This signing link is invalid or expired. Contact ClientsOnRamp to reissue.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0E1A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const c = data.contract;
  const isClient = data.kind === "client";
  const isSigned = isClient ? !!c.party_a_signed_at : !!c.party_b_signed_at;

  if (done || isSigned) {
    return (
      <div className="min-h-screen bg-[#0A0E1A] text-white flex items-center justify-center p-8">
        <div className="max-w-lg text-center border border-emerald-500/40 p-10 bg-[#141B2D]" data-testid="sign-done">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400 mb-4" />
          <h1 className="font-display text-3xl font-bold mb-2">Signed</h1>
          <p className="text-neutral-300">
            Your <strong className="text-white">{c.package_name}</strong> agreement is fully executed.
            A signed PDF has been archived in ClientsOnRamp's records{isClient ? "; your contractor agreement will kick off automatically." : "."}
          </p>
          <p className="text-neutral-500 text-sm mt-6 font-mono">
            Contract № {c.id.slice(0, 8).toUpperCase()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white p-6" data-testid="public-sign-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-3xl mx-auto py-10">
        <div className="flex items-center gap-2 mb-8">
          <Building2 className="w-5 h-5 text-[#FF6B47]" />
          <span className="font-display text-xl font-bold">ClientsOnRamp</span>
        </div>

        <div className="text-xs font-mono uppercase tracking-widest text-[#F5B840] mb-2 flex items-center gap-2">
          <FileSignature className="w-3.5 h-3.5" />
          {isClient ? "Client services agreement" : "Contractor agreement"}
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold mb-2" style={{ letterSpacing: "-0.03em" }}>
          {c.package_name}
        </h1>
        <div className="text-neutral-400 text-sm mb-8">Contract № <span className="font-mono">{c.id.slice(0, 8).toUpperCase()}</span></div>

        <div className="grid gap-6 mb-8">
          <div className="border border-neutral-800 bg-[#141B2D] p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Scope</div>
            <p className="text-neutral-200 leading-relaxed">{c.package_description}</p>
            {c.scope_notes && (
              <div className="mt-3 pt-3 border-t border-neutral-800 text-neutral-300 text-sm whitespace-pre-wrap">{c.scope_notes}</div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-neutral-800 bg-[#141B2D] p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">
                {isClient ? "Fee payable to ClientsOnRamp" : "Payout to you"}
              </div>
              <div className="text-4xl font-bold text-[#FF6B47]">${(isClient ? c.sell_price : c.contractor_payout).toLocaleString()}</div>
            </div>
            <div className="border border-neutral-800 bg-[#141B2D] p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Deliver by</div>
              <div className="text-2xl font-bold">{c.delivery_by?.slice(0, 10)}</div>
              <div className="text-xs text-neutral-500 mt-1">{c.delivery_days} calendar days from today</div>
            </div>
          </div>
        </div>

        <div className="border border-neutral-800 bg-[#141B2D] p-6 mb-8">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">Key terms</div>
          <ol className="space-y-2 text-sm text-neutral-300 list-decimal ml-5">
            <li>Party B performs the scope above and delivers by the date shown.</li>
            <li>Fee due upon completion unless otherwise agreed; late payments accrue 1.5%/mo.</li>
            <li>Deliverables become the property of Party A upon full payment.</li>
            <li>Confidentiality maintained for 3 years post-agreement.</li>
            <li>Either party may terminate with 14 days' written notice; work-to-date invoiced.</li>
            <li>Governed by the laws of the State of California, USA.</li>
          </ol>
        </div>

        <form onSubmit={sign} className="border border-[#FF6B47] bg-[#141B2D] p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-[#FF6B47] mb-4 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Sign to accept
          </div>
          <label className="flex items-start gap-2 text-sm text-neutral-300 mb-4 cursor-pointer">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" data-testid="sign-agree" />
            <span>
              I have read and agree to the {isClient ? "services agreement" : "contractor agreement"} above.
              I understand my typed name below is my legally binding electronic signature.
            </span>
          </label>
          <label className="grid gap-1 mb-4">
            <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">Full legal name</span>
            <input
              autoFocus required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First Last"
              data-testid="sign-name-input"
              className="bg-transparent border-b-2 border-neutral-700 text-2xl font-display italic focus:outline-none focus:border-[#FF6B47] py-2"
              style={{ fontStyle: "italic" }}
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="sign-submit"
            className="w-full bg-[#FF6B47] hover:bg-[#E24E1B] text-white px-5 py-4 font-bold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing…</> : <><FileSignature className="w-4 h-4" /> Sign & execute</>}
          </button>
          <div className="text-xs text-neutral-500 mt-3 text-center font-mono">
            Your IP address is recorded on signature for audit compliance.
          </div>
        </form>
      </div>
    </div>
  );
}
